import { ClaimEscalationRule } from '../../prisma/enums';

/**
 * Fleet exception-escalation rules (CUR-504 extended).
 *
 * Each rule is a clock: it starts at a lifecycle moment, and is *met* when the
 * expected next action happens. If the threshold elapses with the rule unmet,
 * one notice goes to the fleet client's account manager and never repeats.
 *
 * The met condition is the important half. Without it a rule is a timer that
 * fires on every healthy claim rather than an exception detector, so a rule
 * that cannot express "this went fine" does not belong here.
 */
export const CLAIM_ESCALATION_RULE_DEFINITIONS = {
    [ClaimEscalationRule.REPAIRER_NOT_ASSIGNED]: {
        label: 'Repairer not assigned',
        /** Clock starts when the claim is lodged. */
        startsAt: 'Claim lodged',
        /**
         * Met once a quote request has gone to a repairer. A desktop assessment
         * does not count — that is an assessor being engaged, not a repairer.
         */
        metWhen: 'A quote is requested from a repairer',
    },
    [ClaimEscalationRule.QUOTE_NOT_RECEIVED]: {
        label: 'Quote not received',
        startsAt: 'Quote requested from the repairer',
        metWhen: 'A usable quote arrives on the assessment register',
    },
    [ClaimEscalationRule.QUOTE_NOT_ASSESSED]: {
        label: 'Quote not assessed',
        startsAt: 'Quote received from the repairer',
        /**
         * Sending the quote to an assessor answers it just as a decision does —
         * both are someone acting on the quote rather than leaving it to sit.
         */
        metWhen: 'The quote is authorised, declined, or sent for desktop assessment',
    },
} as const satisfies Record<ClaimEscalationRule, { label: string; startsAt: string; metWhen: string }>;

export type ClaimEscalationRuleSetting = {
    isEnabled: boolean;
    /**
     * Minutes from the rule's clock start before the claim is escalated.
     * Dialable per org so a demo can breach in ten minutes while production
     * keeps a realistic SLA.
     */
    thresholdMinutes: number;
};

export type FleetEscalationConfiguration = {
    /** Master switch. Off means no escalation is evaluated or sent for the org. */
    isEnabled: boolean;
    /**
     * ISO-8601 instant. Claims created at or before it are never escalated.
     *
     * Set this when enabling an org. Without it, switching on a ten-minute
     * threshold would find every in-flight claim already past its clock and
     * notify the account manager about all of them at once — one email per
     * claim, all describing history nobody can act on.
     */
    onlyClaimsCreatedAfter: string | null;
    rules: Record<ClaimEscalationRule, ClaimEscalationRuleSetting>;
};

/**
 * Off by default in every org, with production-shaped thresholds so that
 * enabling the feature never silently inherits demo timings.
 *
 * All three targets are one day (CUR-618). The clock is plain elapsed minutes,
 * so a Friday-afternoon breach is reported on Saturday; a business-day-aware
 * clock is productisation work, not a default anyone should approximate by
 * padding the threshold.
 */
export const DEFAULT_FLEET_ESCALATION_CONFIGURATION: FleetEscalationConfiguration = {
    isEnabled: false,
    onlyClaimsCreatedAfter: null,
    rules: {
        [ClaimEscalationRule.REPAIRER_NOT_ASSIGNED]: { isEnabled: true, thresholdMinutes: 24 * 60 },
        [ClaimEscalationRule.QUOTE_NOT_RECEIVED]: { isEnabled: true, thresholdMinutes: 24 * 60 },
        [ClaimEscalationRule.QUOTE_NOT_ASSESSED]: { isEnabled: true, thresholdMinutes: 24 * 60 },
    },
};

/** Internal names of the escalation email templates, one per rule. */
export const CLAIM_ESCALATION_TEMPLATE_NAMES = {
    [ClaimEscalationRule.REPAIRER_NOT_ASSIGNED]: 'claim_FleetEscalationRepairerNotAssigned',
    [ClaimEscalationRule.QUOTE_NOT_RECEIVED]: 'claim_FleetEscalationQuoteNotReceived',
    [ClaimEscalationRule.QUOTE_NOT_ASSESSED]: 'claim_FleetEscalationQuoteNotAssessed',
} as const satisfies Record<ClaimEscalationRule, string>;
